/**
 * Money formatting, pinned to en-US (contracts v3, UI part §4).
 *
 * `toLocaleString()` without a locale follows the *browser*, so the same invoice reads "18,348"
 * in one visitor's browser and "18 348" or "18.348" in another's — and a German locale turns
 * `1234.5` into `1.234,5`, which reads as a thousand-times-larger number next to a plain one.
 * The app is English-only ([[app-i18n-is-empty-stub]]), so every amount is formatted here.
 *
 * Two shapes exist because the wire has two: api's display amounts arrive as plain numbers, while
 * contract events carry base units as strings (6 decimals for USDT/USDC/EURC) — the tracker shows
 * both in the same line, so both must land on the same formatting.
 */

const LOCALE = 'en-US';

/** A display amount as api quotes it (already in whole tokens). */
export const formatAmount = (value: number, maximumFractionDigits = 2): string => {
  // The dash, not "NaN"/"∞": this is the funnel every screen amount passes through, and a
  // malformed wire value must read as missing, never as a number-shaped developer leak.
  if (!Number.isFinite(value)) {
    return '—';
  }

  return value.toLocaleString(LOCALE, { maximumFractionDigits });
};

/** `formatAmount` with the token symbol appended: "18,348 USDC". */
export const formatMoney = (value: number, symbol: string, maximumFractionDigits = 2): string =>
  `${formatAmount(value, maximumFractionDigits)} ${symbol}`;

/**
 * Base units → whole tokens, without floating point on the way.
 *
 * `Number(baseUnits) / 10 ** decimals` loses precision above 2^53 base units (≈ 9 billion tokens
 * at 6 decimals), and rounds where it must not: the integer part is split off as a string first,
 * so what is printed is what the contract emitted.
 */
export const fromBaseUnits = (baseUnits: string, decimals: number): number => {
  const digits = baseUnits.trim();

  if (!/^-?\d+$/.test(digits)) {
    return Number.NaN;
  }

  const negative = digits.startsWith('-');
  const absolute = negative ? digits.slice(1) : digits;
  const padded = absolute.padStart(decimals + 1, '0');
  const whole = padded.slice(0, padded.length - decimals);
  const fraction = decimals > 0 ? padded.slice(padded.length - decimals) : '';

  return Number(`${negative ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''}`);
};

/**
 * A contract-emitted amount, formatted for a line of the tracker: "1,000 EURC".
 *
 * Fraction digits follow the token, so a 6-decimal stablecoin never prints a rounded number where
 * the chain holds a precise one — but trailing zeros are dropped (`maximumFractionDigits`), so a
 * whole amount stays "1,000" rather than "1,000.000000".
 */
export const formatBaseUnits = (baseUnits: string, decimals: number, symbol: string): string => {
  const value = fromBaseUnits(baseUnits, decimals);

  return Number.isNaN(value) ? `— ${symbol}` : formatMoney(value, symbol, decimals);
};
